import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';
import type { ContainerType } from '../../ContainerComponents/ContainerPage';
import ContainerTypeThumbnail from './ContainerTypeThumbnail';
import type { EquivalenceOption } from './useContainerTypesAdmin';

const SEGMENT_NONE = '__none__';

type FacetOption = { value: string; label: string };

type ContainerTypeEditFormProps = {
  item: ContainerType;
  language: string;
  disabled: boolean;
  /** Draft (create) mode: shows a #draft identity and hides reordering. */
  isDraft?: boolean;
  containerClasses: FacetOption[];
  containerTypeGroups: FacetOption[];
  countries: FacetOption[];
  brandings: string[];
  legalOwners: string[];
  /** Every container type that can be marked equivalent to this one, with its existing group. */
  equivalenceOptions: EquivalenceOption[];
  duplicateSkus: string[];
  onField: (attr: string, value: unknown) => void;
  onCountryToggle: (code: string) => void;
  onEquivalentToggle: (typeId: number) => void;
  onAddBranding: (value: string) => void;
  onAddLegalOwner: (value: string) => void;
  onReorder: (direction: 1 | -1) => void;
  onUploadImage: () => void;
};

/** Search hits shown at once — the full type list is thousands of entries long. */
const EQUIVALENCE_RESULT_LIMIT = 10;

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
      {children}
    </div>
  );
}

function EquivalenceChip({
  option,
  selected,
  disabled,
  groupExtra = 0,
  onClick,
}: {
  option: { value: number; label: string };
  selected: boolean;
  disabled: boolean;
  /** Partners that get pulled in with this one, shown as a "+n" hint. */
  groupExtra?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={
        groupExtra > 0
          ? i18n.t('EditContainerInfo:equivalentsGroupHint', {
              id: option.value,
              name: option.label,
              count: groupExtra,
            })
          : `#${option.value} ${option.label}`
      }
      className={cn(
        'tw:inline-flex tw:h-7 tw:items-center tw:justify-center tw:rounded-md tw:px-2.5 tw:text-xs tw:font-semibold tw:transition-colors',
        disabled ? 'tw:cursor-not-allowed tw:opacity-50' : 'tw:cursor-pointer',
        selected
          ? 'tw:border tw:border-primary tw:bg-primary tw:text-primary-foreground'
          : 'tw:border tw:border-border tw:bg-background tw:text-muted-foreground',
      )}
    >
      {option.label}
      {groupExtra > 0 && (
        <span className="tw:ml-1.5 tw:font-normal tw:opacity-70">
          +{groupExtra}
        </span>
      )}
    </button>
  );
}

export default function ContainerTypeEditForm({
  item,
  language,
  disabled,
  isDraft = false,
  containerClasses,
  containerTypeGroups,
  countries,
  brandings,
  legalOwners,
  equivalenceOptions,
  duplicateSkus,
  onField,
  onCountryToggle,
  onEquivalentToggle,
  onAddBranding,
  onAddLegalOwner,
  onReorder,
  onUploadImage,
}: ContainerTypeEditFormProps) {
  const nameValue = item.namesByLanguage?.[language] ?? item.name ?? '';
  const hex = (item.hexCode || '').toString();
  const hexWithHash = `#${hex.padEnd(6, '0').slice(0, 6) || '000000'}`;
  const skuInvalid = !!item.sku && duplicateSkus.includes(item.sku);
  const selectedCountries = item.countryCodes || [];
  const selectedEquivalents = React.useMemo(
    () => item.containerEquivalentTypeIds ?? [],
    [item.containerEquivalentTypeIds],
  );

  // A type is never equivalent to itself. There are far too many types to list
  // them all, so only the selected ones plus a handful of search hits show up.
  const [equivalenceSearch, setEquivalenceSearch] = React.useState('');
  const { selectedChoices, searchChoices, hiddenMatchCount } =
    React.useMemo(() => {
      const byLabel = (a: { label: string }, b: { label: string }): number =>
        a.label.localeCompare(b.label);
      const others = equivalenceOptions.filter(
        (option) => option.value !== item.id,
      );
      // Selected ids drive this list, not the option list: an id the option list
      // does not know (yet) still has to show up, or it would look unselected
      // while being saved.
      const selected = selectedEquivalents
        .filter((typeId) => typeId !== item.id)
        .map(
          (typeId) =>
            others.find((option) => option.value === typeId) ?? {
              value: typeId,
              label: `#${typeId}`,
            },
        )
        .sort(byLabel);

      const term = equivalenceSearch.trim().toLowerCase();
      if (term === '')
        return {
          selectedChoices: selected,
          searchChoices: [],
          hiddenMatchCount: 0,
        };

      const matches = others
        .filter((option) => !selectedEquivalents.includes(option.value))
        .filter(
          (option) =>
            option.label.toLowerCase().includes(term) ||
            String(option.value).includes(term),
        )
        .sort(byLabel);
      return {
        selectedChoices: selected,
        searchChoices: matches.slice(0, EQUIVALENCE_RESULT_LIMIT),
        hiddenMatchCount: Math.max(
          0,
          matches.length - EQUIVALENCE_RESULT_LIMIT,
        ),
      };
    }, [equivalenceOptions, equivalenceSearch, item.id, selectedEquivalents]);

  // Make sure the currently-selected branding / owner is always an option.
  const brandingOptions = React.useMemo(() => {
    const set = [...brandings];
    if (item.branding && !set.includes(item.branding)) set.push(item.branding);
    return set;
  }, [brandings, item.branding]);
  const ownerOptions = React.useMemo(() => {
    const set = [...legalOwners];
    if (item.legalOwner && !set.includes(item.legalOwner))
      set.push(item.legalOwner);
    return set;
  }, [legalOwners, item.legalOwner]);

  const promptAdd = (labelKey: string, onAdd: (value: string) => void) => {
    if (disabled) return;
    const value = window.prompt(i18n.t(`EditContainerInfo:${labelKey}`));
    if (value && value.trim()) onAdd(value.trim());
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-5 tw:px-5 tw:py-5">
      {/* Identity strip */}
      <div className="tw:flex tw:items-center tw:gap-3.5">
        <ContainerTypeThumbnail
          imageUrl={item.imageUrl}
          hexCode={item.hexCode}
          segment={item.segment}
          size={60}
          onClick={disabled ? undefined : onUploadImage}
          title={nameValue}
        />
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
          <div className="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-muted-foreground">
            <span className="tw:font-mono">
              #{isDraft ? i18n.t('EditContainerInfo:draftId') : item.id}
            </span>
            {!isDraft && (
              <>
                <span className="tw:h-3 tw:w-px tw:bg-border" />
                <span>
                  {i18n.t('EditContainerInfo:orderShort', { n: item.orderKey })}
                </span>
              </>
            )}
          </div>
          <div className="tw:truncate tw:text-base tw:font-semibold">
            {nameValue ||
              (isDraft ? i18n.t('EditContainerInfo:untitledContainer') : '')}
          </div>
        </div>
      </div>

      {/* Basics */}
      <div className="tw:flex tw:flex-col tw:gap-3">
        <SectionHeading>
          {i18n.t('EditContainerInfo:sectionBasics')}
        </SectionHeading>
        <Field>
          <FieldLabel htmlFor="ct-name">
            {i18n.t('EditContainerInfo:fieldName', {
              lang: language.toUpperCase(),
            })}
          </FieldLabel>
          <Input
            id="ct-name"
            value={nameValue}
            disabled={disabled}
            placeholder={i18n.t('EditContainerInfo:placeholderName')}
            onChange={(e) => onField('name', e.target.value)}
          />
        </Field>
        <div className="tw:flex tw:gap-3">
          <Field className="tw:flex-[1.4]">
            <FieldLabel htmlFor="ct-sku">
              {i18n.t('EditContainerInfo:colSku')}
            </FieldLabel>
            <Input
              id="ct-sku"
              value={item.sku || ''}
              disabled={disabled}
              aria-invalid={skuInvalid}
              className="tw:font-mono"
              placeholder="OS ..."
              onChange={(e) =>
                onField('sku', (e.target.value || '').toUpperCase())
              }
            />
            {skuInvalid && (
              <FieldError>
                {i18n.t('EditContainerInfo:duplicateSkuError')}
              </FieldError>
            )}
          </Field>
          <Field className="tw:w-24 tw:flex-none">
            <FieldLabel htmlFor="ct-size">
              {i18n.t('EditContainerInfo:colSize')}
            </FieldLabel>
            <Input
              id="ct-size"
              value={item.sizeHelper || ''}
              disabled={disabled}
              placeholder="M"
              onChange={(e) => onField('sizeHelper', e.target.value)}
            />
          </Field>
        </div>
      </div>

      <Separator />

      {/* Classification */}
      <div className="tw:flex tw:flex-col tw:gap-3">
        <SectionHeading>
          {i18n.t('EditContainerInfo:sectionClassification')}
        </SectionHeading>
        <div className="tw:flex tw:gap-3">
          <Field className="tw:flex-1">
            <FieldLabel>{i18n.t('EditContainerInfo:colClass')}</FieldLabel>
            <Select
              value={item.class || undefined}
              disabled={disabled}
              onValueChange={(v) => onField('class', v)}
            >
              <SelectTrigger className="tw:w-full">
                <SelectValue placeholder={i18n.t('Common:selectOption')} />
              </SelectTrigger>
              <SelectContent>
                {containerClasses.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field className="tw:flex-1">
            <FieldLabel>{i18n.t('EditContainerInfo:colGroup')}</FieldLabel>
            <Select
              value={item.groupId != null ? String(item.groupId) : undefined}
              disabled={disabled}
              onValueChange={(v) => onField('group', v)}
            >
              <SelectTrigger className="tw:w-full">
                <SelectValue placeholder={i18n.t('Common:selectOption')} />
              </SelectTrigger>
              <SelectContent>
                {containerTypeGroups.map((g) => (
                  <SelectItem key={g.value} value={g.value}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field>
          <FieldLabel>{i18n.t('EditContainerInfo:segment')}</FieldLabel>
          <Select
            value={item.segment || SEGMENT_NONE}
            disabled={disabled}
            onValueChange={(v) =>
              onField('segment', v === SEGMENT_NONE ? null : v)
            }
          >
            <SelectTrigger className="tw:w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SEGMENT_NONE}>
                {i18n.t('EditContainerInfo:segmentNone')}
              </SelectItem>
              <SelectItem value="Drinks">
                {i18n.t('EditContainerInfo:segmentDrinks')}
              </SelectItem>
              <SelectItem value="Food">
                {i18n.t('EditContainerInfo:segmentFood')}
              </SelectItem>
              <SelectItem value="Cutlery">
                {i18n.t('EditContainerInfo:segmentCutlery')}
              </SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Separator />

      {/* Availability */}
      <div className="tw:flex tw:flex-col tw:gap-3">
        <div className="tw:flex tw:items-center tw:justify-between">
          <SectionHeading>
            {i18n.t('EditContainerInfo:sectionAvailability')}
          </SectionHeading>
          <span className="tw:text-xs tw:text-muted-foreground">
            {i18n.t('EditContainerInfo:countryCount', {
              count: selectedCountries.length,
            })}
          </span>
        </div>
        <div className="tw:flex tw:flex-wrap tw:gap-1.5">
          {countries.map((country) => {
            const checked = selectedCountries.includes(country.value);
            return (
              <button
                key={country.value}
                type="button"
                disabled={disabled}
                onClick={() => onCountryToggle(country.value)}
                className={cn(
                  'tw:inline-flex tw:h-7 tw:min-w-10 tw:items-center tw:justify-center tw:rounded-md tw:px-2.5 tw:text-xs tw:font-semibold tw:transition-colors',
                  disabled
                    ? 'tw:cursor-not-allowed tw:opacity-50'
                    : 'tw:cursor-pointer',
                  checked
                    ? 'tw:border tw:border-primary tw:bg-primary tw:text-primary-foreground'
                    : 'tw:border tw:border-border tw:bg-background tw:text-muted-foreground',
                )}
              >
                {country.label}
              </button>
            );
          })}
        </div>
      </div>

      <Separator />

      {/* Equivalences — types that share a physical form with this one */}
      <div className="tw:flex tw:flex-col tw:gap-3">
        <div className="tw:flex tw:items-center tw:justify-between">
          <SectionHeading>
            {i18n.t('EditContainerInfo:sectionEquivalents')}
          </SectionHeading>
          <span className="tw:text-xs tw:text-muted-foreground">
            {i18n.t('EditContainerInfo:equivalentCount', {
              count: selectedEquivalents.length,
            })}
          </span>
        </div>
        <p className="tw:text-xs tw:text-muted-foreground">
          {i18n.t('EditContainerInfo:equivalentsHint')}
        </p>
        <Input
          id="ct-equivalents-search"
          value={equivalenceSearch}
          disabled={disabled}
          placeholder={i18n.t('EditContainerInfo:placeholderEquivalentSearch')}
          onChange={(e) => setEquivalenceSearch(e.target.value)}
        />
        {selectedChoices.length > 0 && (
          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {selectedChoices.map((option) => (
              <EquivalenceChip
                key={option.value}
                option={option}
                selected
                disabled={disabled}
                onClick={() => onEquivalentToggle(option.value)}
              />
            ))}
          </div>
        )}
        {equivalenceSearch.trim() === '' ? (
          selectedChoices.length === 0 && (
            <span className="tw:text-xs tw:text-muted-foreground">
              {i18n.t('EditContainerInfo:equivalentsSearchToAdd')}
            </span>
          )
        ) : (
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            {searchChoices.length === 0 ? (
              <span className="tw:text-xs tw:text-muted-foreground">
                {i18n.t('EditContainerInfo:equivalentsNoMatch')}
              </span>
            ) : (
              <div className="tw:flex tw:flex-wrap tw:gap-1.5">
                {searchChoices.map((option) => (
                  <EquivalenceChip
                    key={option.value}
                    option={option}
                    selected={false}
                    disabled={disabled}
                    groupExtra={
                      option.groupTypeIds.filter(
                        (typeId) =>
                          typeId !== option.value &&
                          typeId !== item.id &&
                          !selectedEquivalents.includes(typeId),
                      ).length
                    }
                    onClick={() => onEquivalentToggle(option.value)}
                  />
                ))}
              </div>
            )}
            {hiddenMatchCount > 0 && (
              <span className="tw:text-xs tw:text-muted-foreground">
                {i18n.t('EditContainerInfo:equivalentsMoreMatches', {
                  count: hiddenMatchCount,
                })}
              </span>
            )}
          </div>
        )}
      </div>

      <Separator />

      {/* Packaging & handling */}
      <div className="tw:flex tw:flex-col tw:gap-3">
        <SectionHeading>
          {i18n.t('EditContainerInfo:sectionPackaging')}
        </SectionHeading>
        <div className="tw:flex tw:gap-3">
          <Field className="tw:flex-1">
            <FieldLabel htmlFor="ct-pu">
              {i18n.t('EditContainerInfo:fieldPackingUnit')}
            </FieldLabel>
            <Input
              id="ct-pu"
              value={item.packingUnit ?? ''}
              disabled={disabled}
              className="tw:font-mono"
              inputMode="numeric"
              onChange={(e) =>
                onField(
                  'packingUnit',
                  (e.target.value || '').replace(/[^0-9]/g, ''),
                )
              }
            />
          </Field>
          <Field className="tw:flex-1">
            <FieldLabel htmlFor="ct-weight">
              {i18n.t('EditContainerInfo:fieldWeight')}
            </FieldLabel>
            <Input
              id="ct-weight"
              value={item.weight ?? ''}
              disabled={disabled}
              className="tw:font-mono"
              inputMode="numeric"
              onChange={(e) =>
                onField('weight', (e.target.value || '').replace(/[^0-9]/g, ''))
              }
            />
          </Field>
        </div>
        <Field>
          <FieldLabel htmlFor="ct-pi">
            {i18n.t('EditContainerInfo:fieldPackingInstructions')}
          </FieldLabel>
          <Textarea
            id="ct-pi"
            value={item.packingInstructions || ''}
            disabled={disabled}
            rows={2}
            placeholder={i18n.t(
              'EditContainerInfo:placeholderPackingInstructions',
            )}
            onChange={(e) => onField('packingInstructions', e.target.value)}
          />
        </Field>
      </div>

      <Separator />

      {/* Branding & ownership */}
      <div className="tw:flex tw:flex-col tw:gap-3">
        <SectionHeading>
          {i18n.t('EditContainerInfo:sectionBranding')}
        </SectionHeading>
        <div className="tw:flex tw:gap-3">
          <Field className="tw:min-w-0 tw:flex-1">
            <FieldLabel>{i18n.t('EditContainerInfo:colBranding')}</FieldLabel>
            <div className="tw:flex tw:gap-1.5">
              <div className="tw:min-w-0 tw:flex-1">
                <Select
                  value={item.branding || undefined}
                  disabled={disabled}
                  onValueChange={(v) => onField('branding', v)}
                >
                  <SelectTrigger className="tw:w-full">
                    <SelectValue placeholder={i18n.t('Common:select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {brandingOptions.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="icon"
                type="button"
                disabled={disabled}
                onClick={() => promptAdd('addBrandingPrompt', onAddBranding)}
              >
                <Plus size={15} />
              </Button>
            </div>
          </Field>
          <Field className="tw:min-w-0 tw:flex-1">
            <FieldLabel>{i18n.t('EditContainerInfo:colLegalOwner')}</FieldLabel>
            <div className="tw:flex tw:gap-1.5">
              <div className="tw:min-w-0 tw:flex-1">
                <Select
                  value={item.legalOwner || undefined}
                  disabled={disabled}
                  onValueChange={(v) => onField('legalOwner', v)}
                >
                  <SelectTrigger className="tw:w-full">
                    <SelectValue placeholder={i18n.t('Common:select')} />
                  </SelectTrigger>
                  <SelectContent>
                    {ownerOptions.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="icon"
                type="button"
                disabled={disabled}
                onClick={() =>
                  promptAdd('addLegalOwnerPrompt', onAddLegalOwner)
                }
              >
                <Plus size={15} />
              </Button>
            </div>
          </Field>
        </div>
        <Field>
          <FieldLabel htmlFor="ct-hex">
            {i18n.t('EditContainerInfo:fieldBoxColour')}
          </FieldLabel>
          <div className="tw:flex tw:items-center tw:gap-2">
            <div className="tw:flex tw:max-w-[190px] tw:flex-1 tw:items-center tw:overflow-hidden tw:rounded-md tw:border tw:border-input tw:bg-background tw:shadow-xs">
              <span className="tw:pl-2.5 tw:pr-1 tw:font-mono tw:text-sm tw:text-muted-foreground">
                #
              </span>
              <input
                id="ct-hex"
                value={hex.toUpperCase()}
                disabled={disabled}
                className="tw:h-9 tw:min-w-0 tw:flex-1 tw:border-none tw:bg-transparent tw:font-mono tw:text-sm tw:uppercase tw:tracking-wide tw:text-foreground tw:outline-none"
                onChange={(e) =>
                  onField(
                    'hexCode',
                    (e.target.value || '')
                      .replace(/[^0-9A-Fa-f]/g, '')
                      .slice(0, 6),
                  )
                }
              />
            </div>
            <label
              className="tw:relative tw:size-9 tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:shadow-xs"
              style={{
                backgroundColor: hexWithHash,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="color"
                value={hexWithHash}
                disabled={disabled}
                className="tw:absolute tw:-inset-1 tw:size-11 tw:cursor-pointer tw:border-none tw:p-0 tw:opacity-0"
                onChange={(e) =>
                  onField('hexCode', (e.target.value || '').slice(1))
                }
              />
            </label>
          </div>
        </Field>
      </div>

      <Separator />

      {/* Status & order */}
      <div className="tw:flex tw:flex-col tw:gap-3 tw:pb-1.5">
        <SectionHeading>
          {i18n.t('EditContainerInfo:sectionStatus')}
        </SectionHeading>
        <div
          className="tw:flex tw:items-center tw:justify-between tw:rounded-md tw:border tw:border-border tw:px-3.5 tw:py-3"
          style={
            item.archived
              ? {
                  backgroundColor:
                    'color-mix(in oklab, var(--destructive) 6%, var(--background))',
                }
              : undefined
          }
        >
          <div className="tw:flex tw:flex-col tw:gap-0.5">
            <span className="tw:text-sm tw:font-medium">
              {i18n.t('Common:archived')}
            </span>
            <span className="tw:text-xs tw:text-muted-foreground">
              {i18n.t('EditContainerInfo:archivedHint')}
            </span>
          </div>
          <Switch
            checked={!!item.archived}
            disabled={disabled}
            onCheckedChange={(checked) => onField('archived', checked)}
          />
        </div>
        {!isDraft && (
          <div className="tw:flex tw:items-center tw:justify-between tw:rounded-md tw:border tw:border-border tw:px-3.5 tw:py-3">
            <div className="tw:flex tw:flex-col tw:gap-0.5">
              <span className="tw:text-sm tw:font-medium">
                {i18n.t('EditContainerInfo:displayOrder')}
              </span>
              <span className="tw:text-xs tw:text-muted-foreground">
                {i18n.t('EditContainerInfo:displayOrderHint', {
                  n: item.orderKey,
                })}
              </span>
            </div>
            {/* Stepper: − moves the container down the list, + moves it up
                (raises its display order). */}
            <div className="tw:flex tw:gap-1.5">
              <Button
                variant="outline"
                size="icon"
                type="button"
                disabled={disabled}
                aria-label={i18n.t('Common:moveDown')}
                title={i18n.t('Common:moveDown')}
                onClick={() => onReorder(-1)}
              >
                <Minus size={15} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                type="button"
                disabled={disabled}
                aria-label={i18n.t('Common:moveUp')}
                title={i18n.t('Common:moveUp')}
                onClick={() => onReorder(1)}
              >
                <Plus size={15} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
